import React, { useEffect } from "react";

import styled from "@emotion/styled";

import useAddressApi from "../../../../../../../hooks/useAddressApi";
import AddressFallback from "../../../../../../shared/AddressFallback";
import Field from "../../../../../../shared/forms/Field";
import FormError from "../../../../../../shared/forms/FormError";
import NewInput from "../../../../../../shared/forms/NewInput";
import SelectInput, {
    SalutationSelect,
} from "../../../../../../shared/forms/NewSelect";
import Icon from "../../../../../../shared/Icon";
import Margin from "../../../../../../shared/Margin";
import NewHeadline from "../../../../../../shared/NewHeadline";
import { ZipcodeCityWrapper } from "../../../../../../shared/orders/StyledComponents";
import useSnackbar from "../../../../../../shared/Snackbar/store";
import useCheckoutStore from "../../../store";
import useStepDeliveryAddress from "../../store";

const AddressInputWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const StreetRow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    ${({ theme }) => theme.breakpoints.from.small.css`
       grid-template-columns: 5fr 1fr;
       gap: 10px;
    `}
`;

const CityRow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    ${({ theme }) => theme.breakpoints.from.small.css`
       grid-template-columns: 1fr 5fr;
       gap: 10px;
    `}
`;

const Zipcode = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 1em;
    color: ${({ theme }) => theme.colors.blue.toString()};
`;
const AdditionalAddress = ({ index, showDelete = true }) => {
    const { notify } = useSnackbar();

    const [checkoutData, setCheckoutData] = useCheckoutStore((state) => [
        state.checkoutData,
        state.setCheckoutData,
    ]);

    const [
        deliveryAddress,
        additionalAddresses,
        storeUnloadingPointsToServer,
        decreaseUnloadingPoints,
        addAdditionalAddress,
        updateAdditionalAddress,
        removeAdditionalAddress,
        addError,
        clearError,
        hasErrorAt,
    ] = useStepDeliveryAddress((state) => [
        state.deliveryAddress,
        state.additionalAddresses,
        state.storeUnloadingPointsToServer,
        state.decreaseUnloadingPoints,
        state.addAdditionalAddress,
        state.updateAdditionalAddress,
        state.removeAdditionalAddress,
        state.addError,
        state.clearError,
        state.hasErrorAt,
    ]);

    const { streets, cities, addressApiError } = useAddressApi({
        zipcode: additionalAddresses[index].zipcode,
        city: additionalAddresses[index].city,
    });

    useEffect(() => {
        if (!additionalAddresses[index]) {
            addAdditionalAddress(deliveryAddress);
        }
    }, []);

    const address = additionalAddresses[index];
    /**
     * On change of a value.
     *
     * @param {*} event
     */
    const handleChange = (event) => {
        if (event.target.name === "amount") {
            clearError("additionalAddresses");
        }
        updateAdditionalAddress(
            {
                ...address,
                ...{ [event.target.name]: event.target.value },
            },
            index
        );
    };

    const handleCityChange = (event) => {
        if (event.target.value !== "") {
            clearError(`additionalAddresses.${index}.city`);
        }
        updateAdditionalAddress(
            {
                ...address,
                ...{ city: event.target.value, street: "" },
            },
            index
        );
    };

    const handleStreetChange = (event) => {
        if (event.target.value !== "") {
            clearError(`additionalAddresses.${index}.street`);
        }
        updateAdditionalAddress(
            {
                ...address,
                street: event.target.value ?? "",
            },
            index
        );
    };

    const handleHouseNumberChange = (event) => {
        updateAdditionalAddress(
            {
                ...address,
                house_number: event.target.value,
            },
            index
        );
    };

    const onGooglePlacesChange = (placesData) => {
        const result = placesData[0];

        result.address_components.forEach((addres) => {
            if (address.types.includes("route")) {
                updateAdditionalAddress(
                    {
                        ...address,
                        street: address.long_name,
                    },
                    index
                );
            }
            if (address.types.includes("street_number")) {
                updateAdditionalAddress(
                    {
                        ...address,
                        house_number: address.long_name,
                    },
                    index
                );
            }
            if (address.types.includes("locality")) {
                updateAdditionalAddress(
                    {
                        ...address,
                        city: address.long_name,
                    },
                    index
                );
            }
        });
    };

    const onClickRemoveAdditionalAddress = async () => {
        await removeAdditionalAddress(index);
        await decreaseUnloadingPoints();

        const checkoutDataResponse = await storeUnloadingPointsToServer(
            checkoutData,
            notify
        );
        setCheckoutData(checkoutDataResponse);
    };

    return (
        <React.Fragment>
            <NewHeadline color="blue">
                Haushalt {Number(index) + 2}{" "}
                {showDelete && (
                    <Margin
                        Component={Icon}
                        left="5"
                        type="trash"
                        small
                        onClick={onClickRemoveAdditionalAddress}
                    />
                )}
            </NewHeadline>

            <NewInput
                placeholder="Menge"
                id={`amount_${index}`}
                name="amount"
                onChange={handleChange}
                value={address.amount}
                validation="hel:amount"
                forceError={hasErrorAt(`additionalAddresses.${index}.amount`)}
                onError={() => addError(`additionalAddresses.${index}.amount`)}
                onErrorSolved={() =>
                    clearError(`additionalAddresses.${index}.amount`)
                }
                withField
            />
            {hasErrorAt("additionalAddresses") && (
                <FormError
                    gtag={`error:${hasErrorAt("additionalAddresses")}`}
                    message="Die Summe der Menge der einzelden Haushalte darf nicht der Eingekauften Menge überschreiten."
                />
            )}
            <SalutationSelect
                id={`salutation_${index}`}
                name="salutation"
                onChange={handleChange}
                value={address.salutation}
                withField
            />

            <NewInput
                placeholder="Vorname"
                id={`firstname_${index}`}
                name="firstname"
                onChange={handleChange}
                value={address.firstname}
                validation="name"
                fullWidth
                forceError={hasErrorAt(
                    `additionalAddresses.${index}.firstname`
                )}
                onError={() =>
                    addError(`additionalAddresses.${index}.firstname`)
                }
                onErrorSolved={() =>
                    clearError(`additionalAddresses.${index}.firstname`)
                }
                withField
            />
            <NewInput
                placeholder="Nachname"
                id={`lastname_${index}`}
                name="lastname"
                onChange={handleChange}
                value={address.lastname}
                validation="name"
                forceError={hasErrorAt(`additionalAddresses.${index}.lastname`)}
                onError={() =>
                    addError(`additionalAddresses.${index}.lastname`)
                }
                onErrorSolved={() =>
                    clearError(`additionalAddresses.${index}.lastname`)
                }
                withField
            />
            <NewInput
                placeholder="Telefonnummer"
                id={`phone_${index}`}
                name="phone"
                onChange={handleChange}
                value={address.phone}
                validation="phone"
                forceError={hasErrorAt(`additionalAddresses.${index}.phone`)}
                onError={() => addError(`additionalAddresses.${index}.phone`)}
                onErrorSolved={() =>
                    clearError(`additionalAddresses.${index}.phone`)
                }
                withField
            />
            <Field>
                {!addressApiError ? (
                    <AddressInputWrapper>
                        <CityRow>
                            <Zipcode>{deliveryAddress.zipcode}</Zipcode>
                            <div>
                                <SelectInput
                                    name="city"
                                    placeholder="Bitte wähle Deinen Ort aus"
                                    withDefault
                                    options={cities.map((city) => ({
                                        value: city.ONAME,
                                        text: city.ONAME,
                                    }))}
                                    value={
                                        cities.find((city) => {
                                            return (
                                                city.ONAME ===
                                                additionalAddresses[index].city
                                            );
                                        })?.ONAME ?? ""
                                    }
                                    onChange={handleCityChange}
                                />
                                {hasErrorAt(
                                    `additionalAddresses.${index}.city`
                                ) && (
                                    <FormError
                                        message={"Bitte wähle einen Ort aus."}
                                    />
                                )}
                            </div>
                        </CityRow>

                        <StreetRow>
                            <div>
                                <SelectInput
                                    name="street"
                                    placeholder="Bitte wähle Deine Straße aus"
                                    withDefault
                                    options={streets.map((street) => {
                                        return {
                                            value: street,
                                            text: street,
                                        };
                                    })}
                                    value={
                                        streets.find(
                                            (street) =>
                                                street ===
                                                additionalAddresses[index]
                                                    .street
                                        ) ?? ""
                                    }
                                    onChange={handleStreetChange}
                                />
                                {hasErrorAt(
                                    `additionalAddresses.${index}.street`
                                ) && (
                                    <FormError
                                        message={"Bitte wähle eine Straße aus."}
                                    />
                                )}
                            </div>

                            <NewInput
                                placeholder="Haus.-Nr."
                                id="house_number"
                                name="house_number"
                                onChange={handleHouseNumberChange}
                                value={additionalAddresses[index].house_number}
                                validation="house_number"
                                forceError={hasErrorAt(
                                    `additionalAddresses.${index}.house_number`
                                )}
                                onError={() =>
                                    addError(
                                        `additionalAddresses.${index}.house_number`
                                    )
                                }
                                onErrorSolved={() =>
                                    clearError(
                                        `additionalAddresses.${index}.house_number`
                                    )
                                }
                            />
                        </StreetRow>
                    </AddressInputWrapper>
                ) : (
                    <AddressFallback
                        onGooglePlacesChange={onGooglePlacesChange}
                        zipcode={deliveryAddress.zipcode}
                        handleHouseNumberChange={handleHouseNumberChange}
                        houseNumber={additionalAddresses[index].house_number}
                        houseNumberHasErrorAt={hasErrorAt(
                            "deliveryAddress.house_number"
                        )}
                        houseNumberAddError={() =>
                            addError("deliveryAddress.house_number")
                        }
                        houseNumberClearError={() =>
                            clearError("deliveryAddress.house_number")
                        }
                    />
                )}
            </Field>
        </React.Fragment>
    );
};

export default AdditionalAddress;
